import { useEffect, useState } from 'react';
import { TransactionStatus, ProductSubType, ProductType } from '@ace-clublabs/payment-types';
import { ClubHelper, PolicyHelper } from '@ace-clublabs/payments-helper';
import Spinner from '../components/Spinner';
import { Box, Typography } from '@ace-clublabs/shared-component-library';
import { Confirmation } from '@ace-clublabs/payments-component-library';
import { IConfirmationApiResponse } from '../shared/types';
import { useGlobalState } from '../shared/state';
import { Pages } from '../shared/pages';
import UrlHelper from '../utils/urlHelper';
import { sources } from '../api/sources';
import { useNavigate } from 'react-router-dom';
import { UtagHelper } from '../utils/utagHelper';
import Header from '../components/Header';
import { LogCannon } from '@ace-clublabs/log-cannon';
import {
  getPaymentPBLTaggingFlow,
  getPaymentPBLTaggingInsurancePolicies,
  getPaymentTypeTagging,
} from '../utils/methods';

export const ConfirmationPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(true);
  const [confirmationDetails, setConfirmationDetails] = useGlobalState('confirmationDetails');
  const [payByLinkData] = useGlobalState('payByLinkData');
  const [emails, setEmails] = useGlobalState('emails');
  const [customerId] = useGlobalState('customerId');
  const [sessionId] = useGlobalState('sessionId');
  const [epcRequestId] = useGlobalState('epcRequestId');
  const [paymentType] = useGlobalState('paymentType');
  const [, setPaymentProcessing] = useGlobalState('paymentProcessing');
  const { firstName, clubCode, email } = payByLinkData || {};
  const urlParams = new URLSearchParams(window.location.search);
  const transactionId = urlParams.get('transactionId');

  const fetchConfirmation = async () => {
    if (transactionId) {
      try {
        const sixteenDigitMemberNumber = payByLinkData?.products?.find(
          (item) => item.productType.toLowerCase() === 'membership'
        )?.productId;
        const request = { transactionId, requestId: epcRequestId, customerId };
        let confirmationResponse: IConfirmationApiResponse[] = await sources.confirmation(request);
        const ersAmountPaid =
          confirmationResponse.find((d) => d.productType.toLowerCase() === 'ers')?.paymentDetails?.amountPaid || 0;

        confirmationResponse = confirmationResponse
          .filter((details) => details.productType.toLowerCase() !== 'ers')
          .map((details) => {
            const { productId, productPrefix, productType } = details;
            const isMembership = productType.toLowerCase() === 'membership';
            return {
              ...details,
              productId: isMembership ? sixteenDigitMemberNumber! : `${productPrefix || ''}${productId}`,
              ...(productType && {
                productType: (productType.charAt(0).toUpperCase() + productType.slice(1).toLowerCase()) as ProductType,
              }),
              ...(isMembership &&
                ersAmountPaid > 0 && {
                  paymentDetails: {
                    ...details.paymentDetails,
                    ersAmountPaid,
                  },
                }),
            };
          });
        setConfirmationDetails(confirmationResponse);
        setLoading(false);

        const isSMSFlow = payByLinkData && customerId && sessionId;

        if (isSMSFlow) {
          const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
          const isReload = navEntries.length > 0 && navEntries[0].type === 'reload';
          const getTaggingErrorMessage = (confirmationResponse: any[]): string | undefined => {
            let hasPaymentFailure = false;
            let hasAutoPayFailure = false;
            for (const item of confirmationResponse) {
              const { paymentStatus, autoPayStatus } = item.paymentDetails;
              if (paymentStatus && paymentStatus !== 'succeeded') {
                hasPaymentFailure = true;
              }
              if (autoPayStatus && autoPayStatus !== 'succeeded') {
                hasAutoPayFailure = true;
              }
            }
            if (hasPaymentFailure && hasAutoPayFailure) {
              return 'my account:sms:confirmation-autopay enrollment and payment failed';
            } else if (hasAutoPayFailure) {
              return 'my account:sms:confirmation-autopay enrollment failed';
            } else if (hasPaymentFailure) {
              return 'my account:sms:confirmation-payment failed';
            }
            return undefined;
          };

          const products = payByLinkData?.products || [];
          const productTypes = products.map((product: any) => {
            const productType = product.productType?.toLowerCase();
            const productSubType = product.productSubType?.toLowerCase();

            if (productType === 'insurance') {
              return productSubType === 'auto'
                ? 'auto insurance'
                : productSubType === 'home'
                  ? 'home insurance'
                  : `${productSubType} insurance`;
            }
            return productType === 'membership' ? 'membership' : productType;
          });
          const flow = getPaymentPBLTaggingFlow(payByLinkData);
          const insurancePolicies = getPaymentPBLTaggingInsurancePolicies(payByLinkData);

          const hasAutoPay = confirmationResponse.some(
            (detail) => detail.paymentDetails.autoPayStatus === TransactionStatus.Succeeded
          );
          const transactionType = paymentType ? getPaymentTypeTagging(paymentType) : undefined;

          const category: string[] = [];
          const product: string[] = [];
          const quantity: string[] = [];
          const transaction_amount: string[] = [];

          confirmationResponse.forEach((detail) => {
            const isInsurance = detail.productType.toLowerCase() !== 'membership';
            const categoryType = isInsurance ? 'insurance:sms' : 'membership:sms';

            if (
              detail.paymentDetails.paymentStatus === TransactionStatus.Succeeded &&
              detail.paymentDetails.amountPaid &&
              detail.paymentDetails.amountPaid > 0
            ) {
              category.push(categoryType);
              product.push('regular');
              quantity.push('1');
              transaction_amount.push(detail.paymentDetails.amountPaid.toFixed(2));
            }

            if (detail.paymentDetails.autoPayStatus === TransactionStatus.Succeeded) {
              category.push(categoryType);
              product.push('autopay');
              quantity.push('1');
              transaction_amount.push('0.00');
            }
          });

          const tagData: any = {
            autopay: hasAutoPay ? 'true' : 'false',
            channel: 'my account',
            custID: customerId,
            flow,
            ins_policy: insurancePolicies,
            page: 'my account:sms:confirmation',
            sessionid: sessionId,
            subchannel1: 'sms',
            error: getTaggingErrorMessage(confirmationResponse),
            confirmation_id: confirmationResponse[0]?.confirmationNumber || '',
            ...(isReload
              ? {}
              : {
                  category,
                  product,
                  quantity,
                  transaction_amount,
                  ...(transactionType ? { transaction_type: transactionType } : {}),
                }),
          };
          UtagHelper.tag(tagData);
        }
      } catch (error) {
        const errorMessage = 'ERROR: Fail to Fetch Confirmation for PBL';

        LogCannon.blast('Navigating to Error', {
          component: 'ConfirmationPage',
          errorMessage,
          error: typeof error === 'string' ? `${error}` : error,
        });
        navigate(Pages.Error);
      }
    } else {
      const errorData = {
        data: payByLinkData ?? 'missing',
        customerId: customerId ?? 'missing',
        sessionId: sessionId ?? 'missing',
      };
      const errorMessage = 'ERROR: Missing Parameters to Fetch Confirmation';
      LogCannon.blast(errorMessage, errorData);
      LogCannon.blast('Navigating to Error', {
        component: 'ConfirmationPage',
        errorMessage,
        error: errorData,
      });
      navigate(Pages.Error);
    }
  };

  useEffect(() => {
    setPaymentProcessing(false);
    fetchConfirmation();
    if (email) setEmails({ isEmailOnFile: true, primaryEmail: email });
  }, []);

  return (
    <>
      {isLoading && <Spinner />}
      {!isLoading && (
        <>
          <Header />
          <ConfirmationView
            confirmationDetails={confirmationDetails}
            firstName={firstName}
            clubCode={clubCode}
            emails={emails}
            transactionId={transactionId || ''}
          />
        </>
      )}
    </>
  );
};

export interface ConfirmationViewProps {
  confirmationDetails: IConfirmationApiResponse[];
  firstName?: string;
  clubCode?: string;
  emails?: {
    primaryEmail?: string | undefined;
    secondaryEmail?: string | undefined;
    isEmailOnFile?: boolean;
  };
  transactionId: string;
}

export const ConfirmationView: React.FC<ConfirmationViewProps> = (props) => {
  const { firstName, clubCode, confirmationDetails, transactionId } = props || {};
  const { primaryEmail, isEmailOnFile } = props.emails || {};
  const clubPhoneNumber = ClubHelper.getClubPhoneNumber(clubCode || '004');
  const [payByLinkData] = useGlobalState('payByLinkData') || [];
  const [customerId] = useGlobalState('customerId') || [];
  const [sessionId] = useGlobalState('sessionId') || [];

  const enrolledInAutoPay: boolean =
    (confirmationDetails || []).filter(
      (details) => details?.paymentDetails?.autoPayStatus === TransactionStatus.Succeeded
    ).length > 0;
  const paymentSucceeded: boolean =
    (confirmationDetails || []).filter(
      (details) => details?.paymentDetails?.paymentStatus === TransactionStatus.Succeeded
    ).length > 0;
  const hasInsuranceProduct: boolean =
    (confirmationDetails || []).filter((details) => details?.productType.toLowerCase() === 'insurance').length > 0;

  let headerText = '';
  if (paymentSucceeded && enrolledInAutoPay) {
    headerText = 'Thank you for your payment and Auto Pay enrollment';
  } else if (paymentSucceeded) {
    headerText = 'Thank you for your payment';
  } else if (enrolledInAutoPay) {
    headerText = 'Thank you for enrolling in Auto Pay';
  }

  const onClick = () => {
    window.location.assign('https://ace.aaa.com/');
  };

  const confirmationProps = {
    title: headerText,
    smsConfirmationDetails: confirmationDetails,
    discontinueAutoPayMessage: enrolledInAutoPay ? true : false,
    autoPayMessage: enrolledInAutoPay ? (
      <Typography variant="b2Regular">
        <strong>Auto Pay: Enrolled</strong> — Starting with your next billing cycle, we'll begin automatically charging
        or debiting your selected payment method.
      </Typography>
    ) : undefined,
    phoneNumber: clubPhoneNumber,
    button: { primary: { buttonText: 'Return home', onClick } },
  };

  const getAAAVisaOfferText = () => {
    if (clubCode === '004' && hasInsuranceProduct) {
      return undefined;
    }

    let amountPaid = 0;
    confirmationDetails.map((details) => {
      amountPaid += details?.paymentDetails?.amountPaid ?? 0;
    });

    const title = `Hey ${firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase() : 'member'},`;
    return amountPaid && amountPaid * 0.03 > 5
      ? {
          title,
          description: `You could have earned <savingsAmount> cash back using your AAA Visa Signature® credit card at AAA.`,
          linkText: 'Learn more',
          savingsAmount: `${(amountPaid * 0.03).toFixed(2)}`,
        }
      : {
          title,
          description: 'Earn cash back on everyday purchases everywhere using your AAA Visa Signature® credit card.',
          linkText: 'Learn more',
        };
  };

  const getEnabledBanners = (): ProductSubType[] => {
    const hasAutoInsurance = confirmationDetails?.some(
      (detail) =>
        detail.productType?.toLowerCase() === 'insurance' &&
        !!detail.productPrefix &&
        PolicyHelper.getProductInfoFromPolicyNumber(detail.productPrefix.toUpperCase()).productSubType ===
          ProductSubType.Auto
    );

    const hasHomeInsurance = confirmationDetails?.some(
      (detail) =>
        detail.productType?.toLowerCase() === 'insurance' &&
        !!detail.productPrefix &&
        PolicyHelper.getProductInfoFromPolicyNumber(detail.productPrefix.toUpperCase()).productSubType ===
          ProductSubType.Home
    );

    if (!hasAutoInsurance) {
      return [ProductSubType.Auto];
    } else if (!hasHomeInsurance) {
      return [ProductSubType.Home];
    }
    return [];
  };

  return (
    <Box className="inner-container">
      <Confirmation
        {...confirmationProps}
        aaaVisaOffer={getAAAVisaOfferText()}
        createAccountHandler={UrlHelper.createAccountOnClick}
        emailModal={{
          display: true,
          handleContinue: async (primaryEmail: string, secondaryEmail: string) => {
            const _primaryEmail = primaryEmail || (isEmailOnFile ? props.emails?.primaryEmail : '');

            const _emails = [_primaryEmail, secondaryEmail].filter(
              (email): email is string => email != null && email.trim() !== ''
            );

            try {
              await sources.sendConfirmationEmail({
                emails: _emails,
                transactionId,
                firstName: props.firstName || 'member',
              });
              LogCannon.blast('Confirmation email sent successfully:', {
                transactionId,
                emailCount: _emails.length,
              });
            } catch (error: any) {
              LogCannon.blast('Failed to send confirmation email:', {
                message: error?.message || 'Unknown error',
                transactionId,
                emailCount: _emails.length,
              });
              throw error;
            }
          },
          emailOnFile: isEmailOnFile ? primaryEmail : undefined,
        }}
        showAcquisitionBanner={{ enabledBanners: getEnabledBanners() }}
      />
    </Box>
  );
};
